/**
 * og-pdp-skin.js — Reskins the Ordergroove PDP app block to match the OLAY design.
 * Scoped to og-offer.og-pdp-skin to avoid affecting cart-drawer OG elements.
 */

(function () {
  'use strict';

  var SKIN_ID = 'og-pdp-skin-css';
  var SCOPE = 'og-offer.og-pdp-skin';

  // Prefix every CSS selector with SCOPE so styles don't leak to cart drawer.
  function scopeRule(rule) {
    rule = rule.trim();
    if (!rule) return rule;
    var atMatch = rule.match(/^(@[^{]+)\{([\s\S]+)\}$/);
    if (atMatch) return atMatch[1] + '{' + scopeRule(atMatch[2]) + '}';
    var braceIdx = rule.indexOf('{');
    if (braceIdx === -1) return rule;
    var selPart  = rule.slice(0, braceIdx);
    var bodyPart = rule.slice(braceIdx);
    var prefixed = selPart.split(',').map(function (s) {
      s = s.trim();
      if (!s) return s;
      if (/^og-offer/.test(s)) {
        return s.indexOf(':not(:defined)') !== -1 ? s : s.replace(/^og-offer/, 'og-offer.og-pdp-skin');
      }
      return SCOPE + ' ' + s;
    }).join(', ');
    return prefixed + bodyPart;
  }

  /* ── Figma CSS ─────────────────────────────────────────────────────────── */
  var SKIN_CSS = [
    /* FOUC guard */
    'og-offer:not(:defined){visibility:hidden;opacity:0}',
    'og-offer{display:block!important;font-family:var(--olay-font-inter,"Inter",sans-serif);padding:0;transition:opacity .2s ease}',
    'og-when{display:block!important}',

    /* ── Heading — Inter 500 14px #161716 ── */
    '.og-heading{margin:0 0 8px;font-family:var(--olay-font-inter,"Inter",sans-serif);font-size:14px;font-weight:500;line-height:1.21em;color:#161716}',

    /* ── Two-card row — gap 16px ── */
    /* align-items:flex-start keeps each card at its own natural height — the onetime card
       stays short when subscribe expands to show the frequency dropdown */
    '.og-cards{display:flex!important;flex-direction:row!important;gap:2%;align-items:flex-start;margin-bottom:12px}',

    /* ── Card base — 8px radius, 1px #D8D8D8 border ── */
    '.og-card{flex:1;min-width:0;display:flex;flex-direction:column;border-radius:8px;overflow:hidden;border:1px solid #D8D8D8;background:#FFF;transition:border-color .15s ease,box-shadow .15s ease}',

    /* ── Selected card highlight — JS-managed classes (.one-active / .sub-active) ── */
    /* One-time: full card dark border (card is short so border wraps cleanly around the pill) */
    '.og-card--onetime.one-active{border-color:#161716;box-shadow:0 8px 16px 0 rgba(0,0,0,.1)}',
    /* Subscribe: only shadow — dark border applied to the top pill only (see below) */
    '.og-card--subscribe.sub-active{box-shadow:0 8px 16px 0 rgba(0,0,0,.1)}',

    /* ── Card heights ── */
    '.og-card.og-card--onetime{height:68px}',
    /* Unselected subscribe: min-height so content never clips; overflow:hidden keeps border-radius clean */
    '.og-card.og-card--subscribe{min-height:68px;height:68px;overflow:hidden}',
    '.og-card.og-card--subscribe.sub-active{height:unset;overflow:visible;padding:1%;margin-top:-5px}',
    /* Prices always side-by-side — no wrapping */
    '.og-card--subscribe:not(.sub-active) .og-price-wrap{overflow:hidden}',

    /* ── Radio button base size & position ── */
    '.radio,.btn.radio{margin-left:2%!important;height:12px!important;width:12px!important;flex-shrink:0!important}',

    /* ── Active radio dot — white inner ring on dark background ── */
    '.radio.active::after{border:3.5px solid #fff!important}',

    /* ── og-*-button as card header area ──
       OG SDK renders its own button inside; we style the container + force
       internal <button> to be transparent so our bg shows through ── */
    'og-optout-button,og-optin-button{display:flex!important;align-items:center;width:100%;background:linear-gradient(180deg,#FFF 0%,#F4F4F4 100%)!important;border-bottom:1px solid rgba(216,216,216,.87)!important;cursor:pointer;height:28px;font-size: 12px !important;font-family:Inter!important;font-weight:500!important;position:relative!important;margin-top:0!important}',
    '@media(max-width:767px){og-optout-button,og-optin-button{font-size:10px!important}}',
    /* Force SDK's own <button> to fill the container transparently — shared base reset */
    'og-optout-button button,og-optin-button button{display:flex!important;width:100%!important;margin:0!important;padding:0!important;border:none!important;background:transparent!important;cursor:pointer!important;font-family:var(--olay-font-inter,"Inter",sans-serif)!important;text-align:left!important}',

    /* One-time: column layout (radio row + price below) */
    'og-optout-button button{flex-direction:column!important;align-items:stretch!important}',

    /* Subscribe: single ROW — radio+label left, right-padding reserves space for "Cancel Anytime" span */
    'og-optin-button button{flex-direction:row!important;align-items:center!important;justify-content:flex-start!important;padding:8px 60px 8px 12px!important}',
    '@media(min-width:768px){og-optin-button button{padding:8px 90px 8px 12px!important}}',    /* desktop: more room for nowrap cancel anytime */

    /* One-time top row (radio + label) — gets its own padding */
    'og-optout-button button>*:first-child{display:flex!important;align-items:center!important;gap:10px!important;padding:8px 12px!important}',

    /* Subscribe first child (radio + label group) — button handles padding, takes remaining width */
    'og-optin-button button>*:first-child{display:flex!important;align-items:center!important;gap:10px!important;flex:1!important;padding:0!important}',

    /* "Cancel anytime" — absolutely pinned to right side of the pill, wraps to 2 lines on mobile */
    '.og-cancel-anytime{position:absolute!important;right:0px!important;top:50%!important;transform:translateY(-50%)!important;display:block!important;white-space:normal!important;text-align:center!important;width:44px!important;font-size:8px!important;font-weight:500!important;line-height:1.2em!important;letter-spacing:.01em!important;color:#707070!important;font-family:var(--olay-font-inter,"Inter",sans-serif)!important;pointer-events:none!important}',
    '@media(min-width:768px){.og-cancel-anytime{white-space:nowrap!important;width:auto!important;font-size:10px!important;right:12px!important}}',

    /* SDK label text — broad selectors cover whatever element OG SDK uses */
    'og-optout-button button label,og-optout-button button span,og-optout-button button p,og-optout-button button div,og-optin-button button label,og-optin-button button span,og-optin-button button p,og-optin-button button div{font-size:12px!important;font-weight:500!important;line-height:1em!important;letter-spacing:.01em!important;text-transform:uppercase!important;color:#161716!important;font-family:var(--olay-font-inter,"Inter",sans-serif)!important}',

    /* ── Selected header = dark bg #161716 — driven by JS .one-active / .sub-active ── */
    /* Both: dark header background */
    '.og-card--onetime.one-active og-optout-button,.og-card--subscribe.sub-active og-optin-button{background:#161716!important;color:white;font-weight:500}',
    /* Subscribe only: dark border on the top pill with rounded top corners — card border stays gray */
    '.og-card--subscribe.sub-active og-optin-button{border:1px solid #161716!important;border-radius:7px 7px 0 0!important}',

    /* Selected: wildcard turns all button text white */
    '.og-card--onetime.one-active og-optout-button button *,.og-card--subscribe.sub-active og-optin-button button *{color:#FFF!important}',

    /* "Cancel anytime" keeps its own color when subscribe selected — override the wildcard */
    '.og-card--subscribe.sub-active og-optin-button .og-cancel-anytime{color:#D8D8D8!important}',

    /* ── Price area ── */
    '.og-price-wrap{padding:8px 12px;display:flex;gap:6px;justify-content:space-between;align-items:center;flex-wrap: wrap;}',
    '@media(max-width:767px){.og-price-wrap{padding:8px 8px}}',
    /* Active price-wrap gets dark border on bottom half, completing the pill outline — subscribe only */
    '.og-card--subscribe.sub-active .og-price-wrap{border:1px solid #161716!important;border-radius:0 0 7px 7px!important}',
    '.og-price-row{display:flex!important;flex-direction:row!important;align-items:flex-end!important;gap:8px;flex-wrap:nowrap!important}',

    /* One-time card current price = RED — Figma fill_T0YDQ3 */
    '.og-card--onetime .og-price--current{display:inline!important;font-size:16px!important;font-weight:600!important;line-height:1em!important;letter-spacing:.01em;text-transform:uppercase!important;color:#C11E33!important;opacity:unset}',
      '@media(max-width:767px){.og-card--onetime .og-price--current{font-size:14px!important}}',
    /* Subscribe card current price = dark — Figma fill_94JFWZ */
    '.og-card--subscribe .og-price--current{display:inline!important;font-size:16px!important;font-weight:600!important;line-height:1em!important;letter-spacing:.01em;text-transform:uppercase!important;color:#161716!important}',
    '@media(max-width:767px){.og-card--subscribe .og-price--current{font-size:14px!important}}',
    /* Compare (strikethrough) price — Figma fill_3ODMP9 */
    '.og-price--compare{background: #fff;display:inline!important;font-size:14px!important;font-weight:400!important;line-height:1em!important;color:#707070!important;text-decoration:line-through!important;text-transform:none!important}',
    '@media(max-width:767px){.og-price--compare{font-size:12px!important}}',
    /* ── Discount badge — red pill ── */
    '.og-badge{display:inline-flex;align-items:center;padding:4px 8px;background:#C11E33;border-radius:4px;font-family:var(--olay-font-inter,"Inter",sans-serif);font-size:10px;font-weight:500;line-height:1em;color:#FFF;align-self:flex-start}',

    /* ── Frequency block — hidden when one-time active ── */
    /* .og-frequency-inner IS the bordered box (Figma: stacked label + value inside one border) */
    '.og-frequency-inner{margin-top:10px;padding:10px 16px 0 16px;min-height:48px;border:1px solid #D8D8D8;border-radius:8px;background:#FFF;outline:none}',
    '.og-frequency-inner:focus-visible{outline:2px solid #161716!important;outline-offset:2px!important;border-radius:8px!important}',
    '@media(max-width:767px){.og-frequency-inner{padding:10px 10px 0 10px}}',
    /* Hide when subscribe is NOT selected */
    '.og-card--subscribe:not(.sub-active) .og-frequency-inner{display:none!important}',

    /* Freq label sits inside the bordered box as a small gray label (Figma: "Ships every") */
    'og-text[key="offerEveryLabel"],.og-frequency__label{display:block;margin:0!important;font-family:var(--olay-font-inter,"Inter",sans-serif)!important;font-size:10px!important;font-weight:500!important;line-height:1em;letter-spacing:.01em;color:#707070!important}',

    /* Select: no border, no fixed height — sits below the label inside the same bordered box */
    'og-select-frequency{display:block;border:none;box-shadow:none;outline:none}',
    'og-select-frequency:focus-visible{outline:2px solid #161716!important;outline-offset:2px!important;border-radius:4px!important}',
    'og-select-frequency select{display:block;width:100%;height:auto;padding:2px 32px 2px 0;background:transparent;border:none!important;font-family:var(--olay-font-inter,"Inter",sans-serif);font-size:16px!important;font-weight:500;letter-spacing:.01em;color:#161716;cursor:pointer;appearance:none;-webkit-appearance:none;background-image:url("data:image/svg+xml,%3Csvg width=\'12\' height=\'8\' viewBox=\'0 0 12 8\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M1 1L6 6L11 1\' stroke=\'%23161716\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 8px center}',
    'og-select-frequency select:focus{outline:none}',
    'og-select-frequency select:focus-visible{outline:2px solid #161716!important;outline-offset:2px!important;border-radius:4px!important}',

    /* ── Info row — Figma layout_XWQSUE / style_BH82VX ── */
    '.og-info{display:flex;align-items:flex-start;gap:8px;padding:8px 0;font-family:var(--olay-font-inter,"Inter",sans-serif)!important;font-size:10px!important;font-weight:400!important;line-height:1.21em;color:#161716;position:relative}',
    '.og-info__icon{flex-shrink:0;margin-top:1px}',

    /* ── Tooltip block ── */
    '.tooltip-block{display:inline-flex;align-items:center;position:relative}',
    /* The "i" trigger */
    '.tooltip-i.og-see-details{display:inline-flex;align-items:center;justify-content:center;width:14px;height:14px;border-radius:50%;border:1px solid #161716;font-size:9px;font-weight:700;font-style:italic;font-family:Georgia,serif;color:#161716;cursor:pointer;flex-shrink:0;line-height:1;position:relative;outline:none}',
    /* Tooltip popup — hidden by default */
    '.og-tooltip{display:none;position:absolute;bottom:calc(100% + 8px);left:50%;transform:translateX(-50%);min-width:260px;max-width:300px;background:#FFF;border:1px solid #D8D8D8;border-radius:8px;padding:12px;box-shadow:0 4px 16px rgba(0,0,0,.12);z-index:999;font-family:var(--olay-font-inter,"Inter",sans-serif)}',
    '@media(max-width:1080px){.og-tooltip{transform:translateX(-10%)}}',
    /* Show on hover or focus */
    '.tooltip-i.og-see-details:hover .og-tooltip,.tooltip-i.og-see-details:focus .og-tooltip,.tooltip-i.og-see-details:focus-within .og-tooltip{display:block}',
    /* Tooltip pointer arrow */
    '.og-tooltip-pointer{display:none;position:absolute;bottom:calc(100% + 2px);left:50%;transform:translateX(-50%);width:10px;height:6px;border-left:5px solid transparent;border-right:5px solid transparent;border-top:6px solid #D8D8D8}',
    '.tooltip-i.og-see-details:hover .og-tooltip-pointer,.tooltip-i.og-see-details:focus .og-tooltip-pointer,.tooltip-i.og-see-details:focus-within .og-tooltip-pointer{display:block}',
    /* Tooltip content */
    '.og-popup-main{display:flex;flex-direction:column;gap:10px}',
    '.og-flex{display:flex;flex-direction:row;align-items:flex-start;gap:4px;font-size:11px;font-weight:400;line-height:1.4;color:#161716}',
    '.og-flex>span:first-child{font-size:18px;font-style:italic;font-family:Georgia,serif;color:#D8D8D8;line-height:1;flex-shrink:0;margin-top:-2px}',
    '.og-flex h5{display:inline;font-size:11px;font-weight:700;text-transform:uppercase;color:#161716;font-family:var(--olay-font-inter,"Inter",sans-serif)}',
    '.og-flex a,.og-flex a:visited,.og-flex a:hover{display:inline!important;color:#C11E33!important;text-decoration:underline!important;font-size:inherit!important;pointer-events:auto!important}',

    /* ── Mobile ── */
    '@media(max-width:767px){.og-cards{gap:10px}}',

    /* ── Keyboard accessibility ── */
    /* Remove default focus outline on inner OG elements — card wrapper owns focus */
    'og-optout-button:focus,og-optin-button:focus,og-optout-button:focus-within,og-optin-button:focus-within{outline:none!important}',
    /* Card focus ring — 2px brand-dark, offset so it clears the border */
    '.og-card:focus{outline:none}.og-card:focus-visible{outline:2px solid #161716!important;outline-offset:3px!important;border-radius:10px!important}'
  ].join('\n');

  /* App block wrapper — only styled when OG has rendered (class added in buildLayout) */
  var APP_BLOCK_CSS = [
    '.shopify-block.shopify-app-block.og-block-active{background-color:var(--olay-color-bg-surface);padding:12px;border-top-left-radius:16px;border-top-right-radius:16px}',
    /* No options block following — round bottom corners too */
    '.shopify-block.shopify-app-block.og-block-active.og-block-no-options{border-bottom-left-radius:16px;border-bottom-right-radius:16px}'
  ].join('\n');

  function injectGlobalCSS() {
    if (document.getElementById(SKIN_ID)) return;
    var style = document.createElement('style');
    style.id = SKIN_ID;
    style.textContent = SKIN_CSS.split('\n').map(scopeRule).join('\n') + '\n' + APP_BLOCK_CSS;
    document.head.appendChild(style);
  }

  // CSS injected into og-select shadow root — regular selectors can't pierce shadow DOM.
  var OG_SELECT_CSS = [
    'select{display:block!important;width:100%!important;border:none!important;outline:none!important;background:transparent!important;font-family:var(--olay-font-inter,"Inter",sans-serif)!important;font-size:16px!important;font-weight:500!important;color:#161716!important;cursor:pointer!important;appearance:none!important;-webkit-appearance:none!important;padding:2px 32px 2px 0!important;background-image:url("data:image/svg+xml,%3Csvg width=\'12\' height=\'8\' viewBox=\'0 0 12 8\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M1 1L6 6L11 1\' stroke=\'%23161716\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E")!important;background-repeat:no-repeat!important;background-position:right 8px center!important}',
    '@media(max-width:767px){select{font-size:9px!important}}',
    'select:focus{outline:none!important}',
    'option{font-size:16px!important;font-weight:500!important;color:#161716!important;font-family:var(--olay-font-inter,"Inter",sans-serif)!important;text-transform:capitalize!important}',
    'span{display:none!important}'
  ].join('\n');

  function injectOgSelectCSS(shadowRoot) {
    if (!shadowRoot || shadowRoot._ogSelectSkinned) return;
    shadowRoot._ogSelectSkinned = true;
    var style = document.createElement('style');
    style.textContent = OG_SELECT_CSS;
    shadowRoot.appendChild(style);
  }

  /* ── Inject CSS into a shadow root ─────────────────────────────────────── */
  function injectShadowCSS(shadowRoot) {
    if (!shadowRoot || shadowRoot._ogSkinned) return;
    shadowRoot._ogSkinned = true;
    var style = document.createElement('style');
    style.textContent = SKIN_CSS;
    shadowRoot.insertBefore(style, shadowRoot.firstChild);
  }

  /* ── Recursively inject into any shadow roots inside an element (handles nested shadows) ── */
  function injectIntoShadows(root) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
    var node = walker.currentNode;
    while (node) {
      if (node.shadowRoot) {
        injectShadowCSS(node.shadowRoot);
        /* For og-select specifically: inject bare select/span rules into its shadow root */
        if (node.tagName && node.tagName.toLowerCase() === 'og-select') {
          injectOgSelectCSS(node.shadowRoot);
        }
        /* Recurse to catch nested shadow roots e.g. og-select-frequency > og-select */
        injectIntoShadows(node.shadowRoot);
      }
      node = walker.nextNode();
    }
  }

  // Patches "(Most Popular)" → "(Most common)" inside og-select's shadow DOM options.
  function patchFreqSelectOptions(freqSelectEl) {
    function getOgSelectEl() {
      if (freqSelectEl.shadowRoot) {
        var found = freqSelectEl.shadowRoot.querySelector('og-select');
        if (found) return found;
      }
      return freqSelectEl.querySelector('og-select');
    }

    function getSelectEl() {
      var ogSelectEl = getOgSelectEl();
      if (!ogSelectEl) return null;
      if (ogSelectEl.shadowRoot) {
        injectOgSelectCSS(ogSelectEl.shadowRoot);
        return ogSelectEl.shadowRoot.querySelector('select');
      }
      return null;
    }

    function replaceInOptions(selectEl) {
      Array.from(selectEl.options).forEach(function (opt) {
        Array.from(opt.childNodes).forEach(function (child) {
          if (child.nodeType === Node.TEXT_NODE && /Most Popular/i.test(child.nodeValue)) {
            child.nodeValue = child.nodeValue.replace(/Most Popular/gi, 'Most common');
          }
        });
      });
    }

    function doReplace() {
      var selectEl = getSelectEl();
      if (!selectEl) return false;
      replaceInOptions(selectEl);
      return true;
    }

    var attempts = 0;
    function tryPatch() {
      if (doReplace()) {
        var watchRoot = freqSelectEl.shadowRoot || freqSelectEl;
        var obs = new MutationObserver(function () { doReplace(); });
        obs.observe(watchRoot, { childList: true, subtree: true, characterData: true });
        return;
      }
      if (++attempts < 60) setTimeout(tryPatch, 50);
    }
    tryPatch();
  }

  // Syncs .one-active / .sub-active classes and ARIA state from OG SDK [subscribed] attribute.
  function updateSubState(offerEl, skipPriceUpdate) {
    var optin   = offerEl.querySelector('og-optin-button');
    var cardSub = offerEl.querySelector('.og-card--subscribe');
    var cardOne = offerEl.querySelector('.og-card--onetime');
    var freqSelect = offerEl.querySelector('og-select-frequency');
    var subSelected = optin && optin.hasAttribute('subscribed');
    if (cardSub) {
      cardSub.classList.toggle('sub-active', !!subSelected);
      cardSub.setAttribute('aria-checked', subSelected ? 'true' : 'false');
      /* a11y: WCAG 4.1.2 - Remove card from tab order when selected to prevent nested focusable controls.
         When selected, only the frequency selector inside should be focusable.
         Card remains clickable and keyboard-navigable via arrow keys in keyboard event handlers. */
      cardSub.setAttribute('tabindex', subSelected ? '-1' : '0');
    }
    if (cardOne) {
      cardOne.classList.toggle('one-active', !subSelected);
      cardOne.setAttribute('aria-checked', subSelected ? 'false' : 'true');
      cardOne.setAttribute('tabindex', '0');
    }
    /* a11y: WCAG 4.1.2 - Frequency selector is only focusable when subscribe card is selected.
       This ensures only ONE focusable element is active at a time, preventing nested interactive controls. */
    if (freqSelect) {
      freqSelect.setAttribute('tabindex', subSelected ? '0' : '-1');
    }
    
    // Apply price update only when radio button changes (not on initial load)
    if (!skipPriceUpdate) {
      // updateProductPrice();
      
      // Check if Club Olay is active via class on product-price--root
      const productPriceRoot = document.querySelectorAll(".product-price--root")[0];
      const hasClubOlayActive = productPriceRoot && productPriceRoot.classList.contains("club-olay-active");
      
      if (!hasClubOlayActive) {
        return;
      }
      
      setTimeout(function(){
        // applyClubOlayDiscount();
      },50)
      
    }
  }

  // Removes inner shadow <button> elements from tab order and hides them from screen readers.
  // OG renders role="radio" buttons inside each shadow root which duplicate our .og-card controls.
  // Uses 'inert' attribute to fully prevent assistive technology interaction with nested controls.
  // This fixes WCAG 4.1.2 - tabindex="-1" alone doesn't prevent all AT from accessing nested controls.
  function patchInnerShadowButtons(customEl) {
    function applyPatch(root) {
      root.querySelectorAll('button').forEach(function (btn) {
        // Use inert to completely remove nested buttons from AT tree
        // This is superior to tabindex="-1" + aria-hidden which doesn't fully work
        btn.setAttribute('inert', '');
        btn.setAttribute('tabindex', '-1');
        btn.setAttribute('aria-hidden', 'true');
      });
    }
    function tryPatch() {
      if (!customEl.shadowRoot) return false;
      applyPatch(customEl.shadowRoot);
      if (!customEl._innerBtnObs) {
        var obs = new MutationObserver(function () { applyPatch(customEl.shadowRoot); });
        obs.observe(customEl.shadowRoot, {
          childList: true, subtree: true, attributes: true,
          attributeFilter: ['tabindex', 'aria-hidden', 'inert']
        });
        customEl._innerBtnObs = obs;
      }
      return true;
    }
    if (!tryPatch()) {
      var attempts = 0;
      var retryId = setInterval(function () {
        if (tryPatch() || ++attempts > 20) clearInterval(retryId);
      }, 50);
    }
  }

  function el(tag, cls, html) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html) e.innerHTML = html;
    return e;
  }

  // Replaces SDK-rendered label text and watches for re-renders.
  function watchTextOverride(element, find, replace) {
    function doReplace() {
      var walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
      var node;
      while ((node = walker.nextNode())) {
        if (node.nodeValue && node.nodeValue.trim().toLowerCase() === find.toLowerCase()) {
          node.nodeValue = replace;
        }
      }
    }
    doReplace();
    var obs = new MutationObserver(doReplace);
    obs.observe(element, { childList: true, subtree: true, characterData: true });
  }

  function getJoinClubUrl() {
    var modalTooltip = document.querySelector('.login-modal--tooltip');
    // If the modal indicates the customer is already signed in, do nothing (keep "#" anchor)
    if (modalTooltip && modalTooltip.getAttribute('data-state') === 'signed-in') {
      return '#';
    }

    var headerJoinClubLink = document.querySelector('.login-modal--button-secondary');
    if (headerJoinClubLink && headerJoinClubLink.href) {
      return headerJoinClubLink.href;
    }

    // No fallback URL: if there's no header link, do nothing (keep '#').
    return '#';
  }

  function buildLayout(offerEl) {
    if (offerEl._ogSkinned) return;
    offerEl._ogSkinned = true;
    offerEl.classList.add('og-pdp-skin');

    var ogWhen = offerEl.querySelector('og-when');
    if (!ogWhen) {
      ogWhen = document.createElement('og-when');
      ogWhen.setAttribute('test', 'regularEligible');
      offerEl.appendChild(ogWhen);
    }

    var optout      = offerEl.querySelector('og-optout-button');
    var optin       = offerEl.querySelector('og-optin-button');
    var prices      = Array.from(offerEl.querySelectorAll('og-price'));
    var regPrices   = prices.filter(function (p) { return p.hasAttribute('regular'); });
    var subPrice    = prices.find(function (p) { return p.hasAttribute('subscription'); });
    var regularPrice = regPrices[0];
    var comparePrice = regPrices[1] || null;
    var incentive   = offerEl.querySelector('og-incentive-text');
    incentive.setAttribute('initial', 'true');
    var freqText    = offerEl.querySelector('og-text[key="offerEveryLabel"]');
    var freqSelect  = offerEl.querySelector('og-select-frequency');

    if (!optout)      { optout = document.createElement('og-optout-button'); }
    if (!optin)       { optin  = document.createElement('og-optin-button');  }
    if (!regularPrice) { regularPrice = document.createElement('og-price'); regularPrice.setAttribute('regular', ''); }
    if (!subPrice)    { subPrice = document.createElement('og-price'); subPrice.setAttribute('subscription', ''); }
    if (!comparePrice) { comparePrice = document.createElement('og-price'); comparePrice.setAttribute('regular', ''); }
    if (!incentive)   { incentive = document.createElement('og-incentive-text'); incentive.setAttribute('from', 'DiscountPercent'); }
    if (!freqText)    { freqText = document.createElement('og-text'); freqText.setAttribute('key', 'offerEveryLabel'); }
    if (!freqSelect)  { freqSelect = document.createElement('og-select-frequency'); freqSelect.setAttribute('default-text', '(Most Popular)'); freqSelect.setAttribute('tabindex', '-1'); } /* a11y: initially non-focusable, enabled when subscribe card is selected */

    /* Apply classes to price elements — do NOT touch og-optout/optin internals */
    regularPrice.className = 'og-price--current';
    subPrice.className     = 'og-price--current';
    comparePrice.className = 'og-price--compare';
    freqText.className     = 'og-frequency__label';

    /* ── Build DOM structure ────────────── */
    ogWhen.innerHTML = '';

    /* Heading */
    var headingEl = el('p', 'og-heading', 'Select purchase frequency:');
    var headingId = 'og-freq-heading-' + Math.random().toString(36).slice(2, 8);
    headingEl.id = headingId;
    ogWhen.appendChild(headingEl);

    /* Cards row — radiogroup */
    var cards = el('div', 'og-cards');
    cards.setAttribute('role', 'radiogroup');
    cards.setAttribute('aria-labelledby', headingId);

    /* — One-time card — */
    var cardOne = el('div', 'og-card og-card--onetime');
    cardOne.setAttribute('role', 'radio');
    cardOne.setAttribute('aria-label', 'One-Time Purchase');
    cardOne.setAttribute('aria-checked', 'true');  /* corrected by updateSubState */
    cardOne.setAttribute('tabindex', '0');
    /* Keep inner OG element out of tab order — card wrapper is the focusable unit */
    optout.setAttribute('tabindex', '-1');
    cardOne.appendChild(optout);
    var pw1 = el('div', 'og-price-wrap');
    var pr1 = el('div', 'og-price-row');
    pr1.appendChild(regularPrice);
    pw1.appendChild(pr1);
    cardOne.appendChild(pw1);
    cards.appendChild(cardOne);

    var cardSub = el('div', 'og-card og-card--subscribe');
    cardSub.setAttribute('role', 'radio');
    cardSub.setAttribute('aria-label', 'Subscribe & Save');
    cardSub.setAttribute('aria-checked', 'false');
    cardSub.setAttribute('tabindex', '0');
    optin.setAttribute('tabindex', '-1');
    if (!optin.querySelector('.og-cancel-anytime')) {
      var cancelSpan = document.createElement('span');
      cancelSpan.className = 'og-cancel-anytime';
      cancelSpan.textContent = 'Cancel Anytime';
      optin.appendChild(cancelSpan);
    }
    cardSub.appendChild(optin);

    var pw2 = el('div', 'og-price-wrap');
    var pr2 = el('div', 'og-price-row');
    pr2.appendChild(subPrice);
    pr2.appendChild(comparePrice);
    pw2.appendChild(pr2);

    var badge = el('div', 'og-badge');
    badge.appendChild(incentive);
    badge.appendChild(document.createTextNode(' off'));
    pw2.appendChild(badge);
    cardSub.appendChild(pw2);

    var freqInner = el('div', 'og-frequency-inner');
    /* a11y: removed tabindex="0" - container is not interactive, only child freqSelect is */
    freqInner.appendChild(freqText);
    freqInner.appendChild(freqSelect);
    cardSub.appendChild(freqInner);
    cards.appendChild(cardSub);

    ogWhen.appendChild(cards);

    cardOne.addEventListener('keydown', function (e) {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault(); optout.click();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault(); optin.click(); 
        /* a11y: When subscribe card is selected, it has tabindex="-1" but can still be programmatically focused */
        cardSub.focus();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault(); optin.click(); cardSub.focus();
      }
    });
    cardSub.addEventListener('keydown', function (e) {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault(); optin.click();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault(); optout.click(); cardOne.focus();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault(); optout.click(); cardOne.focus();
      } else if (e.key === 'Tab' && !e.shiftKey) {
        /* a11y: When Tab is pressed on selected subscribe card, move focus to frequency selector */
        var isSelected = cardSub.classList.contains('sub-active');
        if (isSelected && freqSelect) {
          e.preventDefault();
          freqSelect.focus();
        }
      }
    });
    /* a11y: WCAG 4.1.2 - Frequency selector keyboard navigation
       Tab from card -> selector, Shift+Tab from selector -> card, Escape -> toggle to one-time */
    freqSelect.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        optout.click();
        cardOne.focus();
      } else if (e.key === 'Tab' && e.shiftKey) {
        /* Shift+Tab moves back to subscribe card (even though it has tabindex="-1") */
        e.preventDefault();
        cardSub.focus();
      }
    });

    watchTextOverride(freqText, 'Deliver Every', 'Ships every');
    watchTextOverride(optout, 'Deliver one-time only', 'One-Time Purchase');
    watchTextOverride(optin, 'Subscribe to save', 'Subscribe & Save');
    patchFreqSelectOptions(freqSelect);

    var tooltipHTML =
      '<div class="tooltip-block">' +
        '<span class="tooltip-i og-see-details" tabindex="0">i' +
          '<div class="og-tooltip og-tooltip-part" role="tooltip">' +
            '<div class="og-details-content-wrapper">' +
              '<div class="og-popup-main">' +
                '<div class="og-flex" tabindex="0"><span>1&nbsp;</span>' +
                  '<div><h5>NEVER RUN OUT -</h5>&nbsp;Your subscription order will automatically deliver your favorite Olay products based on the schedule you set.</div>' +
                '</div>' +
                '<div class="og-flex" tabindex="0"><span>2&nbsp;</span>' +
                  '<div><h5>Always OLAY.com Best Price &amp; FREE GIFTS -</h5>&nbsp;Always enjoy extra savings and free gifts with every Subscription order.</div>' +
                '</div>' +
                '<div class="og-flex" tabindex="0"><span>3&nbsp;</span>' +
                  '<div><h5>EASY TO UPDATE -</h5>&nbsp;You can pause, skip, change or cancel your Subscription online anytime. To learn more visit our <a href="/contact-us?component=faq" target="_blank">FAQ page</a>.</div>' +
                '</div>' +
              '</div>' +
            '</div>' +
          '</div>' +
          '<div class="og-tooltip-pointer og-tooltip-part"></div>' +
        '</span>' +
      '</div>';
    var infoText = '<span><a href="' + getJoinClubUrl() + '" class="og-join-club-link product-club-olay--link">Join Club Olay</a> for FREE to manage subscriptions and get access to exclusive deals and more!</span>';
    ogWhen.appendChild(el('div', 'og-info', tooltipHTML + infoText));

    /* Inject CSS into any shadow roots the SDK may have created */
    injectIntoShadows(offerEl);

    /* Disable tab stop and hide from screen readers on inner shadow <button> elements.
       Without this, OG's own buttons steal Tab focus from our .og-card wrappers and
       cause screen readers to announce duplicate role="radio" entries. */
    patchInnerShadowButtons(optout);
    patchInnerShadowButtons(optin);

    /* ── Activate wrapper bg/padding only when OG confirms eligibility ──
       Poll og-offer's own offsetHeight — our CSS never touches og-offer directly
       so its height is 0 only when OG truly hides it for ineligible products. */
    var appBlock = offerEl.closest('.shopify-block.shopify-app-block');
    if (appBlock) {
      var eligAttempts = 0;
      function syncBlockActive() {
        eligAttempts++;
        if (offerEl.offsetHeight > 0) {
          appBlock.classList.add('og-block-active');
          /* Check if the parent form block is followed by a visible options block */
          var parentBlock = appBlock.closest('.product-form--block');
          var nextBlock = parentBlock && parentBlock.nextElementSibling;
          var hasOptions = nextBlock &&
                           nextBlock.getAttribute('data-block-type') === 'options' &&
                           nextBlock.offsetHeight > 0;
          appBlock.classList.toggle('og-block-no-options', !hasOptions);
        } else if (eligAttempts < 20) {
          setTimeout(syncBlockActive, 100);
        }
      }
      setTimeout(syncBlockActive, 100);
    }

    /* ── Set initial sub-active state ── */
    updateSubState(offerEl, true); // Skip price update on initial load

    /* ── Watch for OG SDK re-renders and [subscribed] attribute changes ────
       OG SDK often re-renders og-offer children asynchronously (e.g. after
       fetching prices from the API). When that happens our .og-cards wrapper
       gets wiped. Detect removal of .og-cards and rebuild; also watch the
       [subscribed] attribute to keep the frequency block in sync. ── */
    if (offerEl._ogInnerObserver) offerEl._ogInnerObserver.disconnect();

    var priceUpdateTimeout;
    var innerObs = new MutationObserver(function (mutations) {
      /* Check if our custom wrapper is still in the DOM */
      if (!offerEl.querySelector('.og-cards')) {
        /* OG SDK wiped our structure — reset and schedule a rebuild */
        offerEl._ogSkinned = false;
        innerObs.disconnect();
        delete offerEl._ogInnerObserver;
        setTimeout(function () { skinOffer(offerEl); }, 150);
        return;
      }
      /* React to [subscribed] attribute toggling */
      var hasSubscribedChange = false;
      mutations.forEach(function (m) {
        if (m.type === 'attributes' && m.attributeName === 'subscribed') {
          hasSubscribedChange = true;
        }
      });
      
      /* Debounce: only call updateSubState once even if subscribed changes multiple times */
      if (hasSubscribedChange) {
        clearTimeout(priceUpdateTimeout);
        priceUpdateTimeout = setTimeout(function () {
          updateSubState(offerEl, false); // Apply price update on user change
        }, 100);
      }
    });

    innerObs.observe(offerEl, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['subscribed']
    });
    offerEl._ogInnerObserver = innerObs;
  }

  /* ── True when offerEl lives inside the cart drawer (skip skinning there) ── */
  function isInCartDrawer(el) {
    return !!(el.closest(
      'cart-element[data-view="drawer"],' +   // new theme cart drawer
      '[data-view="drawer"],' +               // any element with drawer view attribute
      '#shopify-section-cart-drawer,' +       // Shopify section wrapper
      '#CartDrawer,' +                        // common third-party ID
      '[id*="cart-drawer"],' +               // ID containing "cart-drawer"
      '.cart-drawer,' +                       // class-based
      '[data-cart-drawer],' +                 // data-attribute
      'cart-drawer'                           // custom element tag
    ));
  }

  /* ── Process a single og-offer, retrying until og-when child exists ──────── */
  function skinOffer(offerEl) {
    if (offerEl._ogSkinned || isInCartDrawer(offerEl)) return;
    /* If SDK hasn't rendered og-when yet, poll briefly */
    var attempts = 0;
    var maxAttempts = 20;
    function tryBuild() {
      attempts++;
      /* Build as soon as og-optout-button OR og-when is present (or on max attempts) */
      if (offerEl.querySelector('og-optout-button') || offerEl.querySelector('og-when') || attempts >= maxAttempts) {
        buildLayout(offerEl);
      } else {
        setTimeout(tryBuild, 100);
      }
    }
    tryBuild();
  }

  /* ── Main init ──────────────────────────────────────────────────────────── */
  function init() {
    injectGlobalCSS();

    /* Process any og-offer elements already in the DOM */
    document.querySelectorAll('og-offer').forEach(skinOffer);

    /* Watch for og-offer elements added later (OG renders after SDK loads) */
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.tagName === 'OG-OFFER') {
            skinOffer(node);
          } else if (node.querySelectorAll) {
            node.querySelectorAll('og-offer').forEach(skinOffer);
          }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ── Price update functionality ──────────────────────────────────────────── */
  function updateProductPrice() {
    const priceCurrentEls = document.querySelectorAll('.og-price--current');
    const productPriceOriginalEl = document.querySelector('.product-price--original');
    
    if (!priceCurrentEls || !priceCurrentEls[0] || !productPriceOriginalEl) return null;
    
    const subscribeCard = document.querySelector('.og-card--subscribe');
    const onetimeCard = document.querySelector('.og-card--onetime');
    
    if (!subscribeCard || !onetimeCard) return null;
    
    let priceValue = null;
    
    // Check which card is selected
    if (subscribeCard.classList.contains('sub-active')) {
      // Subscription selected - get value from subscriptionPrice
      if (priceCurrentEls[0].__discountedProductPriceFromOffers && 
          priceCurrentEls[0].__discountedProductPriceFromOffers.subscriptionPrice) {
        priceValue = priceCurrentEls[0].__discountedProductPriceFromOffers.subscriptionPrice;
      }
    } else if (onetimeCard.classList.contains('one-active')) {
      // One-time selected - get value from regularPrice
      if (priceCurrentEls[0].__discountedProductPriceFromOffers && 
          priceCurrentEls[0].__discountedProductPriceFromOffers.regularPrice) {
        priceValue = priceCurrentEls[0].__discountedProductPriceFromOffers.regularPrice;
      }
    }
    
    if (priceValue !== null) {
      productPriceOriginalEl.textContent = priceValue;
      return priceValue;
    }
      const drawerPriceEl = document.querySelector('.product-buy-buttons--drawer-price');
      if (drawerPriceEl) {
        drawerPriceEl.textContent = priceValue;
      }
    return null;
  }

  function applyClubOlayDiscount() {
    const discountTypeEl = document.querySelector('.club_olay_discount_type');
    const discountValueEl = document.querySelector('.club_olay_discount_value');
    const productPriceOriginalEl = document.querySelector('.product-price--original');
    
    if (!discountTypeEl || !discountValueEl || !productPriceOriginalEl) {
      return;
    }
    
    const discountType = discountTypeEl.value.trim();
    const discountValueStr = discountValueEl.value.trim();
    const discountValue = parseFloat(discountValueStr);
    
    if (isNaN(discountValue)) {
      return;
    }
    
    // Get current price from element
    const currentPriceStr = productPriceOriginalEl.textContent.replace(/[^0-9.]/g, '');
    const currentPrice = parseFloat(currentPriceStr);
    
    if (isNaN(currentPrice)) {
      return;
    }
    
    let finalPrice = currentPrice;
    
    if (discountType === 'Fixed-Amount') {
      // Subtract fixed discount value
      finalPrice = currentPrice - discountValue;
    } else if (discountType === 'Percentage') {
      // Apply percentage discount
      finalPrice = currentPrice - (currentPrice * (discountValue / 100));
    }
    
    // Ensure price doesn't go negative
    finalPrice = Math.max(0, finalPrice);
    
    // Format and update price (preserve currency symbol if present)
    const currencySymbol = productPriceOriginalEl.textContent.match(/[^0-9.]/)?.[0] || '$';
    const formattedPrice = currencySymbol + finalPrice.toFixed(2);
    productPriceOriginalEl.textContent = formattedPrice;
    
    // Also update drawer price if it exists
    const drawerPriceEl = document.querySelector('.product-buy-buttons--drawer-price');
    if (drawerPriceEl) {
      drawerPriceEl.textContent = formattedPrice;
    }
    
    // Calculate and update discount percentage in badge
    const discountPercent = currentPrice > 0 ? Math.round(((currentPrice - finalPrice) / currentPrice) * 100) : 0;
    const badgeTextEl = document.querySelector('.product-club-olay--text');
    const stickyPromoEl = document.querySelector('.pdp-sticky-promo span');
    
    if (badgeTextEl && discountPercent > 0) {
      badgeTextEl.textContent = 'Unlocked - ' + discountPercent + '% Off with Club Olay';
    }
    
    if (stickyPromoEl && discountPercent > 0) {
      stickyPromoEl.textContent = 'Unlocked - ' + discountPercent + '% Off with Club Olay';
    }
  }

  function handlePSSSelection() {
    // Only apply Club Olay discount if club-olay-active class exists
    const productPriceRoot = document.querySelectorAll(".product-price--root")[0];
    if (!productPriceRoot || !productPriceRoot.classList.contains("club-olay-active")) {
      return;
    }
    
    const pssSelectedBtn = document.querySelector('.pss__btn.pss__btn--selected');
    if (!pssSelectedBtn) return;
    
    const pssPriceEl = pssSelectedBtn.querySelector('.pss__price-sale');
    if (!pssPriceEl) return;
    
    const pssPriceStr = pssPriceEl.textContent.replace(/[^0-9.]/g, '');
    const pssPrice = parseFloat(pssPriceStr);
    
    if (isNaN(pssPrice)) return;
    const discountTypeEl = document.querySelector('.club_olay_discount_type');
    const discountValueEl = document.querySelector('.club_olay_discount_value');
    const productPriceOriginalEl = document.querySelector('.product-price--original');
    const productPriceCompareEl = document.querySelector('.product-price--compare');
    
    if (!discountTypeEl || !discountValueEl || !productPriceOriginalEl) return;
    
    const discountType = discountTypeEl.value.trim();
    const discountValue = parseFloat(discountValueEl.value.trim());
    
    if (isNaN(discountValue)) return;
    
    let discountedPrice = pssPrice;
    
    if (discountType === 'Fixed-Amount') {
      discountedPrice = pssPrice - discountValue;
    } else if (discountType === 'Percentage') {
      discountedPrice = pssPrice - (pssPrice * (discountValue / 100));
    }
    
    discountedPrice = Math.max(0, discountedPrice);
    
    const currencySymbol = '$';
    productPriceOriginalEl.textContent = currencySymbol + discountedPrice.toFixed(2);
    
    if (productPriceCompareEl) {
      productPriceCompareEl.textContent = currencySymbol + pssPrice.toFixed(2);
    }
    
    // Calculate and update discount percentage in badge
    const discountPercent = pssPrice > 0 ? Math.round(((pssPrice - discountedPrice) / pssPrice) * 100) : 0;
    const badgeTextEl = document.querySelector('.product-club-olay--text');
    const stickyPromoEl = document.querySelector('.pdp-sticky-promo span');
    
    if (badgeTextEl && discountPercent > 0) {
      badgeTextEl.textContent = 'Unlocked - ' + discountPercent + '% Off with Club Olay';
    }
    
    if (stickyPromoEl && discountPercent > 0) {
      stickyPromoEl.textContent = 'Unlocked - ' + discountPercent + '% Off with Club Olay';
    }
  }

  function initPSSListener() {
    document.addEventListener('click', function (e) {
      const pssBtn = e.target.closest('.pss__btn');
      if (!pssBtn) return;
      
      setTimeout(function () {
        if (pssBtn.classList.contains('pss__btn--selected')) {
          // handlePSSSelection();
        }
      }, 100);
    });
  }

  /* ── Bootstrap: wait for OG SDK, then init ─────────────────────────────── */
  if (typeof customElements !== 'undefined' && customElements.whenDefined) {
    customElements.whenDefined('og-offer').then(function () {
      init();
      initPSSListener();
    }).catch(function () {
      /* SDK failed to define og-offer — still inject CSS & watch DOM */
      init();
      initPSSListener();
    });
  } else {
    /* No customElements support — run on DOMContentLoaded */
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        init();
        initPSSListener();
      });
    } else {
      init();
      initPSSListener();
    }
  }
})();
